//// Application Configuration

//// Application Packages
import React, { useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import axios from 'axios'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import { selectForms, updateFormAuxiliary } from '../../reducers/formSlice'

//// Application Components
import DataTableComponent from './file-upload-data-table-component'
import FileUploaderItem from './file-upload-item-component'
import FileSelectorComponent from './file-selector-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components
import { FileUploaderDropContainer, Button } from '@carbon/react'

//// Carbon Icons
import { Attachment } from '@carbon/icons-react'

//// Static Constants

//////// Component
const FileUploadComponent = () => {
    //////// Hooks
    const dispatch = useDispatch()

    //////// State
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const page_digital_name = settingsData?.['page']?.['digital_name']
    const form_digital_name = settingsData?.['form']?.['digital_name']
    const max_file_size = settingsData?.['application']?.['max_file_size']
    const min_file_size = settingsData?.['application']?.['min_file_size']

    //////// Search Params

    //////// Methods

    // Orchestrate File Upload (Integration)
    const orchestrateFileUpload = async (fileData) => {
        try {
            if (fileData) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_UTILITY_ENDPOINT_FILE_UPLOAD}`
                const url = base_url + api_endpoint

                // Create Request
                if (fileData) {
                    const { data } = await axios.post(url, fileData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    })

                    if (data) {
                        return await data
                    } else {
                        console.log('File Upload: Response Is Undefined')
                        return null
                    }
                }
            } else {
                console.log('File Upload: Request Is Undefined')
                return null
            }
        } catch (error) {
            console.warn('Orchestrate File Upload Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Generate Rows For Data Table
    const generateRows = () => {
        // Destructure File Attachment Auxilary Object
        const { forms_with_files, uploaded_file_types, uploaded_file_names } =
            formsData['file-attachments'].auxiliary

        const table = {
            rows: [],
            indeces: [],
            uploads: [],
        }

        // Find Indeces Of Matching Table Digital Name
        if (
            form_digital_name === 'file-attachments' ||
            page_digital_name === 'file-upload'
        ) {
            if (forms_with_files?.length > 0) {
                forms_with_files?.forEach((el, i) => {
                    table.indeces.push(i)
                })
            }
        } else if (form_digital_name) {
            if (forms_with_files?.length > 0) {
                forms_with_files?.forEach((el, i) => {
                    if (el === form_digital_name) {
                        table.indeces.push(i)
                    }
                })
            }
        }

        // Convert Filtered Indeces Into Uploads
        if (table?.['indeces']?.length > 0) {
            if (
                uploaded_file_types?.length > 0 &&
                uploaded_file_names?.length > 0
            ) {
                table?.indeces?.forEach((el) => {
                    table.rows.push({
                        id: uploaded_file_names?.[el],
                        document_type: uploaded_file_types?.[el],
                        document_name: uploaded_file_names?.[el],
                        remove_file: uploaded_file_names?.[el],
                    })
                })
            }
        }

        if (table?.rows) {
            return table?.rows
        } else {
            return null
        }
    }

    // Generate Headers For Data Table
    const generateHeaders = () => {
        const table = {
            headers: [],
        }

        if (
            formsData?.['file-attachments']?.['auxiliary']?.['dataTableHeaders']
                ?.length > 0
        ) {
            formsData?.[
                'file-attachments'
            ]?.auxiliary?.dataTableHeaders?.forEach((el) => {
                table?.headers.push(el)
            })
        }

        if (table?.headers) {
            return table?.headers
        } else {
            return null
        }
    }

    // Handle Deletion Of Loaded File
    const handleFileLoadDelete = async () => {
        try {
            // Update Loaded Files Array
            dispatch(
                updateFormAuxiliary({
                    digital_name: 'file-attachments',
                    auxiliary: {
                        loaded_files: [],
                    },
                })
            )

            // Update Loaded Files Metadata Array
            dispatch(
                updateFormAuxiliary({
                    digital_name: 'file-attachments',
                    auxiliary: {
                        loaded_files_metadata: [],
                    },
                })
            )
        } catch (error) {
            console.warn('Handle File Load Delete Error:', error)
            return null
        }
    }

    // Handle Deletetion Of Uploaded File
    const handleFileUploadDelete = async (e) => {
        try {
            if (e?.target?.name) {
                // Destructure File Attachment Auxilary Object
                const { uploaded_file_names } =
                    formsData['file-attachments'].auxiliary

                // Check If Uploaded Files Array Has Files
                if (uploaded_file_names?.length > 0) {
                    const index = uploaded_file_names.indexOf(e?.target?.name)
                    if (index > -1) {
                        // Destructure File Attachment Auxilary Object
                        const {
                            forms_with_files,
                            uploaded_file_types,
                            uploaded_file_names,
                            system_uploaded_file_names,
                        } = formsData['file-attachments'].auxiliary

                        // Check For File Attachments Required Data & Information
                        if (
                            forms_with_files &&
                            uploaded_file_types &&
                            uploaded_file_names &&
                            system_uploaded_file_names
                        ) {
                            // Update Uploaded Files Array
                            if (system_uploaded_file_names?.length > 1) {
                                const uploads = [...system_uploaded_file_names]
                                if (uploads?.length > 0) {
                                    uploads.splice(index, 1)
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                system_uploaded_file_names:
                                                    uploads,
                                            },
                                        })
                                    )
                                } else {
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                system_uploaded_file_names: [],
                                            },
                                        })
                                    )
                                }
                            } else {
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            system_uploaded_file_names: [],
                                        },
                                    })
                                )
                            }

                            // Update Uploaded File Names Array
                            if (uploaded_file_names?.length > 1) {
                                const uploads = [...uploaded_file_names]
                                if (uploads?.length > 0) {
                                    uploads.splice(index, 1)
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                uploaded_file_names: uploads,
                                            },
                                        })
                                    )
                                } else {
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                uploaded_file_names: [],
                                            },
                                        })
                                    )
                                }
                            } else {
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            uploaded_file_names: [],
                                        },
                                    })
                                )
                            }

                            // Update Forms With Files Array
                            if (forms_with_files?.length > 1) {
                                const forms = [...forms_with_files]
                                forms.splice(index, 1)
                                if (forms?.length > 0) {
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                forms_with_files: forms,
                                            },
                                        })
                                    )
                                } else {
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                forms_with_files: [],
                                            },
                                        })
                                    )
                                }
                            } else {
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            forms_with_files: [],
                                        },
                                    })
                                )
                            }

                            // Update Uploaded File Types Array
                            if (uploaded_file_types?.length > 1) {
                                const types = [...uploaded_file_types]
                                if (types?.length > 0) {
                                    types.splice(index, 1)
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                uploaded_file_types: types,
                                            },
                                        })
                                    )
                                } else {
                                    dispatch(
                                        updateFormAuxiliary({
                                            digital_name: 'file-attachments',
                                            auxiliary: {
                                                uploaded_file_types: [],
                                            },
                                        })
                                    )
                                }
                            } else {
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            uploaded_file_types: [],
                                        },
                                    })
                                )
                            }
                        } else {
                            console.warn(
                                'Upload Delete Failed: No Uploaded Files Information'
                            )
                            return null
                        }
                    } else {
                        console.warn('Upload Delete Failed: No File')
                        return null
                    }
                } else {
                    console.warn('Upload Delete Failed: No Uploaded Files')
                    return null
                }
            }
        } catch (error) {
            console.warn('File Upload Delete Error:', error)
            return null
        }
    }

    // Handle Loading Of File Data
    const handleFileLoad = (e, { addedFiles }) => {
        // Check Added Files Array Has Files
        if (addedFiles?.length > 0) {
            addedFiles.forEach((el, i) => {
                // Only Upload First File From Files Array (One File At A Time Rule)
                if (i === 0) {
                    // Destructure File Attachment Values Object
                    const { FileUpldDcmtType } =
                        formsData['file-attachments'].values

                    // Destructure File Attachment Auxilary Object
                    const {
                        loaded_files,
                        loaded_files_metadata,
                        uploaded_file_types,
                        uploaded_file_names,
                    } = formsData['file-attachments'].auxiliary

                    // Check For File Attachments Required Data & Information
                    if (
                        FileUpldDcmtType &&
                        loaded_files &&
                        loaded_files_metadata &&
                        uploaded_file_types &&
                        uploaded_file_names
                    ) {
                        // Check In Check & Temp Variable Objects
                        const check = {}

                        // File/s Uploaded Count Below 10
                        if (uploaded_file_names?.length < 10) {
                            check.upload_limit = true
                        } else {
                            check.upload_limit = false
                        }

                        // Page/Form Context Check
                        if (page_digital_name) {
                            check.digital_name = true
                        } else if (form_digital_name) {
                            check.digital_name = true
                        } else {
                            check.digital_name = false
                        }

                        // File Type Check
                        if (!el?.['invalidFileType']) {
                            check.valid = true
                        } else {
                            check.valid = false
                        }

                        // Min File Size Check
                        if (el?.['size'] > min_file_size) {
                            check.min_size = true
                        } else {
                            check.min_size = false
                        }

                        // Max File Size Check
                        if (el?.['size'] < max_file_size) {
                            check.max_size = true
                        } else {
                            check.max_size = false
                        }

                        // Document Name Check (Same Document Rule)
                        if (uploaded_file_names?.length > 0) {
                            const index = uploaded_file_names?.indexOf(el?.name)
                            if (index > -1) {
                                check.file_name = false
                            } else {
                                check.file_name = true
                            }
                        } else {
                            check.file_name = true
                        }

                        // Check If File Passes Checks
                        if (
                            check?.upload_limit &&
                            check?.digital_name &&
                            check?.valid &&
                            check?.min_size &&
                            check?.max_size &&
                            check?.file_name
                        ) {
                            console.info('File Upload: Loaded File')

                            // Update Loaded Files Array
                            dispatch(
                                updateFormAuxiliary({
                                    digital_name: 'file-attachments',
                                    auxiliary: {
                                        loaded_files: [...loaded_files, el],
                                    },
                                })
                            )

                            // Update Loaded Files Metadata Array
                            dispatch(
                                updateFormAuxiliary({
                                    digital_name: 'file-attachments',
                                    auxiliary: {
                                        loaded_files_metadata: [
                                            ...loaded_files_metadata,
                                            {
                                                name: el?.name,
                                                status: 'edit',
                                            },
                                        ],
                                    },
                                })
                            )

                            setNotification({
                                type: 'inline',
                                visible: false,
                                kind: '',
                                title: '',
                                subtitle: '',
                            })

                            console.log('File Loaded: Loaded File')
                        } else if (
                            !check?.upload_limit &&
                            check?.digital_name &&
                            check?.valid &&
                            check?.min_size &&
                            check?.max_size &&
                            check?.file_name
                        ) {
                            console.info('File Upload: Failed To Load File')

                            setNotification({
                                type: 'inline',
                                visible: true,
                                kind: 'error',
                                title: 'File Upload Limit Reached:',
                                subtitle:
                                    'Only 10 documents are allowed to be attached to the application. Please ensure 10 or less documents are attached before the application is submitted.',
                            })

                            console.log(
                                'File Upload Failed: File Name Already Name Exists'
                            )
                        } else {
                            console.info('File Upload: Failed To Load File')

                            setNotification({
                                type: 'inline',
                                visible: true,
                                kind: 'error',
                                title: 'File Upload Failed:',
                                subtitle: 'File Name Already Exists',
                            })

                            console.log(
                                'File Upload Failed: File Name Already Name Exists'
                            )
                        }
                    } else {
                        console.warn('File Load Failed: No File Information')
                        return null
                    }
                } else {
                    console.warn('File Load Failed: No File')
                    return null
                }
            })
        } else {
            console.warn('File Load Failed: No Files')
            return null
        }
    }

    // Handle Upload Of File Data
    const handleFileUpload = async () => {
        try {
            // Destructure File Attachment Values Object
            const { FileUpldDcmtType } = formsData['file-attachments'].values

            // Destructure File Attachment Auxilary Object
            const {
                loaded_files,
                forms_with_files,
                uploaded_file_types,
                uploaded_file_names,
                system_uploaded_file_names,
            } = formsData['file-attachments'].auxiliary

            // Check If Loaded Files Array Has Files
            if (loaded_files?.length > 0) {
                // Extract File
                const file = loaded_files?.[0]

                // Check For File Attachments Required Data & Information
                if (
                    file &&
                    FileUpldDcmtType &&
                    loaded_files &&
                    forms_with_files &&
                    uploaded_file_types &&
                    uploaded_file_names &&
                    system_uploaded_file_names
                ) {
                    if (file?.['name']) {
                        // Set File Data To HEaders
                        const fileData = new FormData()
                        fileData.append('upload', file)

                        // Send Orchestration Request
                        const orchestrate = await orchestrateFileUpload(
                            fileData
                        )

                        console.info('File Upload: Passed Orchestrate')

                        // Check For Upload Response
                        if (
                            orchestrate?.files_uploaded &&
                            orchestrate?.file_names_uploaded?.length > 0
                        ) {
                            console.log('File Upload: Success')

                            // Check In Temp Variable Objects
                            const temp = {}

                            // Form/Page Context Check
                            if (form_digital_name) {
                                temp.digital_name = form_digital_name
                            } else if (page_digital_name) {
                                temp.digital_name = page_digital_name
                            }

                            // Check For File Name & Digital Name
                            if (file?.['name'] && temp?.['digital_name']) {
                                // Update System Uploaded File Names Array
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            system_uploaded_file_names: [
                                                ...system_uploaded_file_names,
                                                orchestrate
                                                    ?.file_names_uploaded?.[0],
                                            ],
                                        },
                                    })
                                )

                                // Update Uploaded File Names Array
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            uploaded_file_names: [
                                                ...uploaded_file_names,
                                                file?.name,
                                            ],
                                        },
                                    })
                                )

                                // Update Forms With Files Array
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            forms_with_files: [
                                                ...forms_with_files,
                                                temp?.digital_name,
                                            ],
                                        },
                                    })
                                )

                                // Update Uploaded File Types Array
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            uploaded_file_types: [
                                                ...uploaded_file_types,
                                                FileUpldDcmtType,
                                            ],
                                        },
                                    })
                                )

                                // Update Loaded Files Array
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            loaded_files: [],
                                        },
                                    })
                                )

                                // Update Loaded Files Metadata Array
                                dispatch(
                                    updateFormAuxiliary({
                                        digital_name: 'file-attachments',
                                        auxiliary: {
                                            loaded_files_metadata: [],
                                        },
                                    })
                                )
                            } else {
                                console.warn(
                                    'File Upload: File Name & Digital Name'
                                )
                                return null
                            }
                        } else {
                            console.warn('File Upload: Response Is Undefined')
                            return null
                        }
                    } else {
                        console.warn('File Upload Failed: No File')
                        return null
                    }
                } else {
                    console.warn('File Upload Failed: No File Information')
                    return null
                }
            } else {
                console.warn('File Upload Failed: No Files')
                return null
            }
        } catch (error) {
            console.warn('File Upload Error:', error)
            return null
        }
    }

    //////// Memoization
    const rowData = useMemo(() => generateRows(formsData), [formsData])
    const headerData = useMemo(() => generateHeaders(formsData), [formsData])

    //////// Effects/Side Effects

    //////////////// Render & Return
    if (form_digital_name === 'file-attachments') {
        return (
            <>
                <DataTableComponent
                    title={'File Attachments'}
                    rowData={rowData}
                    headerData={headerData}
                    handleFileUploadDelete={handleFileUploadDelete}
                />

                <p>
                    Max file size is 100MB. Supported file types are .txt, .gif,
                    .jpeg, .jpg, .png, .rtf, .pdf.
                </p>

                <FileSelectorComponent digital_name={form_digital_name} />

                <p>File Attachments:</p>

                <FileUploaderDropContainer
                    className={''}
                    labelText="Drag and drop files here or click to upload, then click the attach button to upload your document. Please upload one document at a time. Also, a Document Type must be selected in order to attach a document."
                    id="FileUpldDcmt"
                    name="FileUpldDcmt"
                    disabled={
                        formsData?.['file-attachments']?.values?.files?.length >
                        0
                            ? true
                            : false
                    }
                    accept={[
                        '.pdf',
                        '.jpeg',
                        '.jpg',
                        '.gif',
                        '.txt',
                        '.png',
                        '.rtf',
                    ]}
                    multiple={false}
                    onChange={handleFileLoad}
                    onAddFiles={handleFileLoad}
                />

                {formsData?.[
                    'file-attachments'
                ]?.auxiliary?.loaded_files_metadata?.map((el, i) => (
                    <FileUploaderItem
                        key={i}
                        name={el?.name}
                        status="edit"
                        handleFileDelete={handleFileLoadDelete}
                    />
                ))}

                <NotificationComponent notification={notification} />

                <div className="half-width-button-with-icon-group">
                    <div className="half-width-button-with-icon-group-item">
                        <Button
                            className="fldoh-btn"
                            type="button"
                            kind="tertiary"
                            size="md"
                            onClick={handleFileUpload}
                        >
                            Attach
                            <Attachment />
                        </Button>
                    </div>
                </div>
            </>
        )
    } else {
        return (
            <>
                <DataTableComponent
                    title={'File Attachments'}
                    rowData={rowData}
                    headerData={headerData}
                    handleFileUploadDelete={handleFileUploadDelete}
                />

                <p>
                    Max file size is 100MB. Supported file types are .txt, .gif,
                    .jpeg, .jpg, .png, .rtf, .pdf.
                </p>

                <FileSelectorComponent digital_name={form_digital_name} />

                <p>File Attachments:</p>

                <FileUploaderDropContainer
                    className={''}
                    labelText="Drag and drop files here or click to upload, then click the attach button to upload your document. Please upload one document at a time."
                    id="FileUpldDcmt"
                    name="FileUpldDcmt"
                    disabled={
                        formsData?.['file-attachments']?.values?.files?.length >
                        0
                            ? true
                            : false
                    }
                    accept={[
                        '.pdf',
                        '.jpeg',
                        '.jpg',
                        '.gif',
                        '.txt',
                        '.png',
                        '.rtf',
                    ]}
                    multiple={false}
                    onChange={handleFileLoad}
                    onAddFiles={handleFileLoad}
                />

                {formsData?.[
                    'file-attachments'
                ]?.auxiliary?.loaded_files_metadata?.map((el, i) => (
                    <FileUploaderItem
                        key={i}
                        name={el?.name}
                        status="edit"
                        handleFileDelete={handleFileLoadDelete}
                    />
                ))}

                <NotificationComponent notification={notification} />

                <div className="half-width-button-with-icon-group">
                    <div className="half-width-button-with-icon-group-item">
                        <Button
                            className="fldoh-btn"
                            type="button"
                            kind="tertiary"
                            size="md"
                            onClick={handleFileUpload}
                        >
                            Attach
                            <Attachment />
                        </Button>
                    </div>
                </div>
            </>
        )
    }
}

export default FileUploadComponent
